import Close16 from '@carbon/icons-react/es/close/16';
import Restart16 from '@carbon/icons-react/es/restart/16';
import Search16 from '@carbon/icons-react/es/search/16';
import Task16 from '@carbon/icons-react/es/task/16';
import { ColumnApi } from 'ag-grid-community';
import { Tooltip, Typography } from 'antd';
import { camelCase } from 'lodash';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from 'components/atoms/checkbox';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { Input } from 'components/atoms/input';
import { Divider } from 'components/molecules/action-panel/components';
import { DraggableList } from 'components/molecules/draggable-list/draggable-list';
import { SearchInput } from 'components/molecules/views/saved-views/components';
import { useTranslation } from 'hooks/use-translation';
import {
  AbsoluteContent,
  CustomMenuItem,
  Header,
  NoResultMessage,
  StyledCheckbox,
  StyledMenu,
  ListContainer,
  MenuItem,
} from './components';
import { ColumnOrder } from './types';
import { useColumnVisible } from './use-column-visible';

const { Text } = Typography;

export interface DropdownMenuProps {
  columnOrder: ColumnOrder[];
  gridColumnApi?: ColumnApi;
  excludedColumns: string[];
  defaultColumnOrder: string[];
}

interface ListData {
  key: string;
  title: string;
  isVisible: boolean;
  hideDrag: boolean;
  render: () => JSX.Element;
}

export const DropdownMenu = ({
  columnOrder,
  gridColumnApi,
  excludedColumns = [],
  defaultColumnOrder,
}: DropdownMenuProps): JSX.Element => {
  const [searchText, setSearchText] = useState('');

  const {
    allColumns,
    isAllColumnsVisible,
    toggleSingleColumnVisible,
    toggleAllColumnsVisible,
    resetDefault,
  } = useColumnVisible(
    columnOrder,
    excludedColumns,
    defaultColumnOrder,
    gridColumnApi,
  );

  const listData = useMemo(
    () =>
      allColumns
        ?.map((col) => {
          const colDef = gridColumnApi?.getColumn(col.colId)?.getColDef();

          if (!colDef) {
            return null;
          }
          const key = colDef?.field as string;
          const title = colDef?.headerName as string;
          const isVisible = !col.hide;

          return {
            key,
            title,
            isVisible,
            hideDrag: !isVisible,
            render: () => (
              <MenuItem
                onClick={() => toggleSingleColumnVisible(key, !isVisible)}
              >
                <Checkbox
                  name={key}
                  checked={isVisible}
                  style={{ padding: '0 var(--spacing-03)', height: 'auto' }}
                  data-cy={`columns_reorder.fields.${camelCase(
                    title,
                  )}.checkbox`}
                />
                <Text
                  ellipsis={{ tooltip: title }}
                  style={{
                    width: 150,
                    lineHeight: 'var(--height-base)',
                  }}
                  data-cy={`columns_reorder.fields.${camelCase(title)}`}
                >
                  {title}
                </Text>
                {key.split('-')[0] === 'project' && key.length > 7 ? (
                  <Tooltip
                    title={
                      <FormattedMessage id="task_list.messages.project_field" />
                    }
                  >
                    <IconWrapper>
                      <Task16 />
                    </IconWrapper>
                  </Tooltip>
                ) : null}
              </MenuItem>
            ),
          };
        })
        .filter(Boolean)
        .filter((col) =>
          searchText
            ? col?.title?.toLowerCase().includes(searchText.toLowerCase())
            : true,
        ) as ListData[],
    [allColumns, gridColumnApi, searchText, toggleSingleColumnVisible],
  );

  return (
    <StyledMenu selectable={false}>
      <Header>
        <FormattedMessage id="ag_grid.edit_columns" />
      </Header>
      <SearchInput>
        <Input
          placeholder={useTranslation('search')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          data-cy="columns_reorder.search-input"
        />
        {searchText ? (
          <Close16
            onClick={() => setSearchText('')}
            style={{ cursor: 'pointer' }}
            data-cy="columns_reorder.clear-search"
          />
        ) : (
          <Search16 />
        )}
      </SearchInput>
      {searchText === '' ? (
        <CustomMenuItem onClick={toggleAllColumnsVisible}>
          <StyledCheckbox
            name="columns.view_all"
            checked={isAllColumnsVisible}
            data-cy="columns_reorder.view-all.checkbox"
          />
          <Header
            style={{ margin: 0, padding: 0 }}
            data-cy="columns_reorder.view-all"
          >
            <FormattedMessage id="ag_grid.view_all_columns" />
          </Header>
        </CustomMenuItem>
      ) : null}
      <ListContainer
        itemLayout="vertical"
        split={false}
        data-cy="drag_handle.wrapper"
      >
        {listData.length > 0 ? (
          <DraggableList
            listData={listData}
            onSortChange={(updatedRow, updatedRowOrder) => {
              gridColumnApi?.moveColumn(
                updatedRow.key,
                updatedRowOrder! + excludedColumns.length,
              );
            }}
          />
        ) : (
          <NoResultMessage>
            <FormattedMessage id="filter.no_results" />
          </NoResultMessage>
        )}
      </ListContainer>
      <AbsoluteContent>
        <Divider style={{ margin: 0 }} />
        <CustomMenuItem
          onClick={resetDefault}
          style={{ justifyContent: 'space-between' }}
          data-cy="columns_reorder.reset_to_default"
        >
          <FormattedMessage id="ag_grid.reset_columns" />
          <IconWrapper style={{ width: 32 }}>
            <Restart16 />
          </IconWrapper>
        </CustomMenuItem>
      </AbsoluteContent>
    </StyledMenu>
  );
};
